import React, { useEffect, useState, useRef } from "react";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Download,
  Trash2,
  AlertCircle,
  Home,
  FileText,
  BarChart2,
  TreePine,
  Bookmark,
  Info,
} from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { getUserReports, ReportMetadata } from "@/services/reportService";
import { REPORT_LIMITS } from "@/utils/subscriptionUtils";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import UserMenu from "@/components/auth/UserMenu";
import { supabase } from "@/integrations/supabase/client";
import { useUserSubscription } from "@/utils/subscriptionUtils";

import TopBar from "@/components/layout/TopBar";
import CreditBalanceDisplay from "@/components/credits/CreditBalanceDisplay";
import { getCreditCost } from "@/types/credits";

const MyReports = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const { theme } = useTheme();
  const { toast } = useToast();
  const [reports, setReports] = useState<ReportMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const removedReportsRef = useRef<ReportMetadata[] | null>(null);
  const deferredDeletionTimeout = useRef<number | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  // Assuming you have a way to determine the user's account tier
  const { tier } = useUserSubscription();
  const [reportLimit, setReportLimit] = useState<{
    allowed: boolean;
    remaining: number;
    message: string;
  } | null>(null);

  // Global per-user report limit (new requirement): 20 reports per user
  const REPORT_MAX = 20;

  const computeLimitFromReports = (list: ReportMetadata[]) => {
    const remaining = Math.max(0, REPORT_MAX - list.length);
    return {
      allowed: remaining > 0,
      remaining,
      message:
        remaining > 0
          ? `${remaining} reports remaining`
          : `Report limit reached (${REPORT_MAX})`,
    };
  };

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    const loadReports = async () => {
      try {
        const userReports = await getUserReports(user.id);
        setReports(userReports);

        // Compute limit based on current reports (20 max per user)
        setReportLimit(computeLimitFromReports(userReports));
      } catch (error) {
        console.error("Error loading reports:", error);
        toast({
          title: "Error loading reports",
          description: "Failed to load your reports. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadReports();
  }, [user, navigate, toast, tier]);

  // Pricing: default per-credit retail price (used for display only)
  const PRICE_PER_CREDIT = 2.99;

  // Filter reports by date range (client-side)
  const filteredReports = React.useMemo(() => {
    return reports.filter((report) => {
      if (!report.created_at) return false;
      const created = new Date(report.created_at);
      if (startDate) {
        const from = new Date(startDate);
        if (created < from) return false;
      }
      if (endDate) {
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        if (created > to) return false;
      }
      return true;
    });
  }, [reports, startDate, endDate]);

  // Compute total spend for displayed (filtered) reports (display-only)
  const totalCreditsForReports =
    getCreditCost("STANDARD_REPORT") * filteredReports.length;
  const totalSpend = totalCreditsForReports * PRICE_PER_CREDIT;

  const getSpendColorClass = (amt: number) => {
    if (amt <= 10) return "text-green-500";
    if (amt <= 20) return "text-amber-500";
    if (amt <= 100) return "text-red-500";
    return "text-red-700";
  };

  const handleDelete = async (reportId: string) => {
    if (!user) return;

    try {
      // Delete report file from storage
      const report = reports.find((r) => r.id === reportId);
      if (report) {
        const filePath = `${user.id}/${report.file_name}`;
        await supabase.storage.from("reports").remove([filePath]);
      }

      // Delete record from database
      const { error } = await supabase
        .from("reports")
        .delete()
        .eq("id", reportId)
        .eq("user_id", user.id);

      if (error) throw error;

      // Update local state
      const updated = reports.filter((report) => report.id !== reportId);
      setReports(updated);

      // Refresh report limits (local computation)
      setReportLimit(computeLimitFromReports(updated));

      // Log deletion to user_activities if available (non-blocking)
      try {
        const reportInfo = report;
        await supabase.from("user_activities").insert([
          {
            user_id: user.id,
            type: "report_deleted",
            description: `Deleted report ${reportInfo?.file_name || reportId} (${reportInfo?.continent || "unknown region"})`,
            created_at: new Date().toISOString(),
          },
        ]);
      } catch (e) {
        // ignore logging failures
      }

      toast({
        title: "Report deleted",
        description: "The report has been successfully deleted.",
      });
    } catch (error) {
      console.error("Error deleting report:", error);
      toast({
        title: "Error deleting report",
        description: "Failed to delete the report. Please try again later.",
        variant: "destructive",
      });
    }
  };

  const toggleSelect = (reportId: string) => {
    setSelectedReportIds((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId],
    );
  };

  const selectAllVisible = (visibleReports: ReportMetadata[]) => {
    const visibleIds = visibleReports
      .map((r) => r.id!)
      .filter(Boolean) as string[];
    if (visibleIds.length === 0) return;
    const allSelected = visibleIds.every((id) =>
      selectedReportIds.includes(id),
    );
    setSelectedReportIds(allSelected ? [] : visibleIds);
  };

  // Open confirmation modal for batch delete
  const handleBatchDelete = async () => {
    if (!user || selectedReportIds.length === 0) return;
    setPendingDeleteIds(selectedReportIds);
    setConfirmModalOpen(true);
  };

  // Confirm deletion: optimistic UI removal + deferred backend deletion with undo
  const confirmBatchDelete = () => {
    if (pendingDeleteIds.length === 0) return;
    // Capture removed reports for undo
    const removed = reports.filter((r) => pendingDeleteIds.includes(r.id!));
    removedReportsRef.current = removed;

    // Optimistically remove from UI
    setReports((prev) => prev.filter((r) => !pendingDeleteIds.includes(r.id!)));
    setSelectedReportIds([]);
    setConfirmModalOpen(false);

    // Show undo toast and schedule actual deletion in 5s
    const t = toast({
      title: "Reports scheduled for deletion",
      description: `${pendingDeleteIds.length} report(s) will be permanently deleted in 5 seconds.`,
      action: (
        <Button
          size="sm"
          onClick={() => {
            // Undo: cancel deferred deletion and restore
            if (deferredDeletionTimeout.current) {
              window.clearTimeout(deferredDeletionTimeout.current as any);
              deferredDeletionTimeout.current = null;
            }
            if (removedReportsRef.current) {
              setReports((prev) => [...removedReportsRef.current!, ...prev]);
              removedReportsRef.current = null;
            }
            toast({ title: "Undo", description: "Deletion cancelled." });
          }}
        >
          Undo
        </Button>
      ),
    });

    const newReports = reports.filter((r) => !pendingDeleteIds.includes(r.id!));
    setReportLimit(computeLimitFromReports(newReports));

    deferredDeletionTimeout.current = window.setTimeout(async () => {
      try {
        const ids = pendingDeleteIds;
        const files = ids
          .map((id) => removed.find((r) => r.id === id))
          .filter(Boolean)
          .map((r) => `${user.id}/${r!.file_name}`);

        if (files.length) {
          await supabase.storage.from("reports").remove(files);
        }

        const { error } = await supabase
          .from("reports")
          .delete()
          .in("id", ids)
          .eq("user_id", user.id);

        if (error) throw error;

        // Refresh report limits (should already reflect optimistic removal, but ensure consistency)
        setReportLimit(computeLimitFromReports(newReports));

        // Log deletions to user_activities where available (non-blocking)
        try {
          const activityInserts = ids.map((id) => {
            const r = removed.find((x) => x.id === id);
            return {
              user_id: user.id,
              type: "report_deleted",
              description: `Deleted report ${r?.file_name || id} (${r?.continent || "unknown region"})`,
              created_at: new Date().toISOString(),
            };
          });
          await supabase.from("user_activities").insert(activityInserts);
        } catch (e) {
          // ignore failures inserting activity logs
        }

        toast({
          title: "Reports deleted",
          description: `${ids.length} report(s) removed.`,
        });
      } catch (err) {
        console.error("Error deleting reports:", err);
        // Revert UI if deletion failed
        if (removedReportsRef.current) {
          setReports((prev) => [...removedReportsRef.current!, ...prev]);
          removedReportsRef.current = null;
        }
        toast({
          title: "Error deleting reports",
          description: "Failed to delete selected reports.",
          variant: "destructive",
        });
      } finally {
        deferredDeletionTimeout.current = null;
        setPendingDeleteIds([]);
      }
    }, 5000);
  };

  const handleDownload = async (report: ReportMetadata) => {
    try {
      // The bucket is private — derive the storage path from user_id + file_name
      // and generate a short-lived signed URL instead of using the stored public URL.
      const filePath = `${report.user_id}/${report.file_name}`;
      const { data, error } = await supabase.storage
        .from("reports")
        .createSignedUrl(filePath, 60); // 60-second expiry

      if (error || !data?.signedUrl) {
        toast({
          title: "File unavailable",
          description:
            "This report file could not be found in storage. It may have been removed. Please regenerate the report from the Dashboard.",
          variant: "destructive",
        });
        return;
      }

      window.open(data.signedUrl, "_blank");
    } catch {
      toast({
        title: "Download failed",
        description:
          "Unable to reach the report file. Please check your connection and try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpgrade = () => {
    navigate("/pricing");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-gradient-to-b from-background to-background/80">
      {/* Collapsible Navbar */}
      <Sidebar />

      <div className="flex-1 flex flex-col">
        {/* Header */}
        {/* Corrected TopBar layout to match the Dashboard's TopBar */}
        <TopBar
          title="My Reports"
          icon={<BarChart2 className="h-4 w-4 text-[#40e0d0]" />}
          showBackButton={false}
          showSubscriptionBadge={true}
        />

        {/* Main Content */}
        <main className="flex-1 container mx-auto px-4 sm:px-6 py-8">
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 mb-6">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 border-muted-foreground/30 text-muted-foreground hover:bg-muted-foreground/10"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>

              {/* Date filter and batch actions */}
              <div className="flex items-center gap-3 mt-4 mb-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">From</label>
                  <input
                    title="Filter reports created from this date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border rounded-md p-2 bg-background"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm text-muted-foreground">To</label>
                  <input
                    title="Filter reports created up to this date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border rounded-md p-2 bg-background"
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  className="ml-2"
                >
                  Clear
                </Button>

                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => selectAllVisible(filteredReports)}
                    className="text-sm"
                  >
                    {selectedReportIds.length > 0
                      ? "Toggle Select Visible"
                      : "Select Visible"}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setViewMode(viewMode === "grid" ? "table" : "grid")
                    }
                    className="text-sm"
                  >
                    {viewMode === "grid" ? "Compact Table" : "Grid View"}
                  </Button>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBatchDelete}
                    disabled={selectedReportIds.length === 0}
                    className="text-sm"
                  >
                    Delete Selected ({selectedReportIds.length})
                  </Button>
                </div>
              </div>
            </div>

            {/* Total reports indicator */}
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground font-bold">
                Total Reports:
              </div>
              <div className="text-xl text-[#40e0d0] font-medium">
                {reports.length}
              </div>
            </div>

            {/** Reports Cost Summary */}
            {reportLimit && (
              <TooltipProvider>
                <div className="flex items-center gap-4 bg-[#f3f4f6] dark:bg-[#272727] px-4 py-2 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground font-medium">
                      <>
                        Reports are stored for 12 months •{" "}
                        <strong>Total spend on reports:</strong>{" "}
                        <strong className={getSpendColorClass(totalSpend)}>
                          R{totalSpend.toFixed(2)}
                        </strong>
                      </>
                    </span>
                  </div>

                  <div className="ml-auto">
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-sm">
                          Displayed costs are estimates. Reports can be
                          re-downloaded at any time within the retention period,
                          at no additional cost.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </TooltipProvider>
            )}

            {tier !== "professional" &&
              reportLimit &&
              reportLimit.remaining === 0 && (
                <Card className="mb-6 border-yellow-500/50 bg-yellow-500/10">
                  <CardContent className="flex items-center gap-3 p-4">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium">
                        You've reached your 30-day report limit
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Upgrade to our Professional plan for unlimited reports
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUpgrade}
                      className="ml-auto border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                    >
                      Upgrade Now
                    </Button>
                  </CardContent>
                </Card>
              )}

            {loading ? (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]" />
              </div>
            ) : reports.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center p-8">
                  <p className="text-muted-foreground text-center">
                    You haven't generated any reports yet. Generate a report
                    from the dashboard to see it here.
                  </p>
                </CardContent>
              </Card>
            ) : filteredReports.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center p-8">
                  <p className="text-muted-foreground text-center mb-4">
                    No reports match the selected date range.
                  </p>
                  <div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {viewMode === "table" ? (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto border-collapse">
                      <thead>
                        <tr className="text-left text-sm text-muted-foreground border-b">
                          <th className="p-3">
                            <input
                              type="checkbox"
                              checked={
                                filteredReports.length > 0 &&
                                filteredReports.every((r) =>
                                  selectedReportIds.includes(r.id!),
                                )
                              }
                              onChange={() => selectAllVisible(filteredReports)}
                              title="Select all visible reports"
                            />
                          </th>
                          <th className="p-3">Report</th>
                          <th className="p-3">Created</th>
                          <th className="p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReports.map((report) => (
                          <tr
                            key={report.id}
                            className="border-b hover:bg-muted/5"
                          >
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedReportIds.includes(report.id!)}
                                onChange={() => toggleSelect(report.id!)}
                                id={`select-report-table-${report.id}`}
                                title={`Select report ${report.continent}`}
                              />
                            </td>
                            <td className="p-3">
                              <div className="font-medium">
                                {report.continent}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {report.file_name}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground">
                              {new Date(report.created_at!).toLocaleString()}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownload(report)}
                                >
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-destructive"
                                  onClick={() => {
                                    setPendingDeleteIds([report.id!]);
                                    setConfirmModalOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredReports.map((report) => (
                      <Card key={report.id} className="dark:bg-[#272727]">
                        <CardHeader className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <label
                              className="sr-only"
                              htmlFor={`select-report-${report.id}`}
                            >
                              Select report {report.continent}
                            </label>
                            <input
                              id={`select-report-${report.id}`}
                              type="checkbox"
                              checked={selectedReportIds.includes(report.id!)}
                              onChange={() => toggleSelect(report.id!)}
                              className="mt-1 h-4 w-4"
                              title={`Select report ${report.continent}`}
                            />
                            <div>
                              <CardTitle className="text-sm font-medium">
                                {report.continent}
                              </CardTitle>
                              <CardDescription className="text-xs text-muted-foreground">
                                Generated on{" "}
                                {new Date(
                                  report.created_at!,
                                ).toLocaleDateString()}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardFooter className="pt-2">
                          <div className="flex items-center gap-2 w-full">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-[#40e0d0] text-[#40e0d0] bg-[#40e0d0]/10 hover:bg-[#40e0d0]/20"
                              onClick={() => handleDownload(report)}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => {
                                setPendingDeleteIds([report.id!]);
                                setConfirmModalOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardFooter>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Floating bulk-action toolbar */}
                {selectedReportIds.length > 0 && (
                  <div className="fixed left-1/2 bottom-6 z-50 -translate-x-1/2 bg-background border border-border shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
                    <div className="text-sm font-medium">
                      {selectedReportIds.length} selected
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setPendingDeleteIds(selectedReportIds);
                        setConfirmModalOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedReportIds([])}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Footer */}
        <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete {pendingDeleteIds.length}{" "}
                report(s)? This action can be undone within a short window.
              </DialogDescription>
            </DialogHeader>
            <div className="pt-4" />
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmBatchDelete}
                className="bg-destructive text-white"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Footer />
      </div>
    </div>
  );
};

export default MyReports;
